// ---------------------------------------------------------------------------
// DSS-12: Box shadow builder — offsets, blur, spread, color, presets, multi
// ---------------------------------------------------------------------------
import { useState, useCallback, useId } from "react";
import { Layers, Plus, Trash2 } from "lucide-react";
import { normalizeColorInputValue } from "./color-input-value";

// ── Types ──
export interface ShadowLayer {
  readonly hOffset: number;
  readonly vOffset: number;
  readonly blur: number;
  readonly spread: number;
  readonly color: string;
  readonly opacity: number;
  readonly isInset: boolean;
}

interface BoxShadowBuilderProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
}

// ── Constants ──
const MIN_OFFSET = -50;
const MAX_OFFSET = 50;
const MIN_BLUR = 0;
const MAX_BLUR = 100;
const MIN_SPREAD = -50;
const MAX_SPREAD = 50;
const MIN_OPACITY = 0;
const MAX_OPACITY = 100;

const DEFAULT_LAYER: ShadowLayer = {
  hOffset: 0, vOffset: 4, blur: 6, spread: 0,
  color: "#000000", opacity: 10, isInset: false,
};

const PRESETS: readonly { readonly name: string; readonly value: string }[] = [
  { name: "None", value: "none" },
  { name: "Subtle", value: "0 1px 2px rgba(0,0,0,0.05)" },
  { name: "Medium", value: "0 4px 6px rgba(0,0,0,0.1)" },
  { name: "Large", value: "0 10px 15px rgba(0,0,0,0.1)" },
  { name: "XL", value: "0 20px 25px rgba(0,0,0,0.15)" },
  { name: "Inner", value: "inset 0 2px 4px rgba(0,0,0,0.1)" },
  { name: "Glow", value: "0 0 20px rgba(59,130,246,0.4)" },
];

// ── Parsing ──
function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`;
}

function buildLayerCss(layer: ShadowLayer): string {
  const inset = layer.isInset ? "inset " : "";
  const rgba = hexToRgba(layer.color, layer.opacity);
  return `${inset}${layer.hOffset}px ${layer.vOffset}px ${layer.blur}px ${layer.spread}px ${rgba}`;
}

function buildShadowCss(layers: ShadowLayer[]): string {
  if (layers.length === 0) return "none";
  return layers.map(buildLayerCss).join(", ");
}

function safeNum(raw: string, fallback: number): number {
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function clamp(val: number, min: number, max: number): number {
  if (Number.isNaN(val)) return min;
  return Math.min(Math.max(val, min), max);
}

// ── Component ──
export default function BoxShadowBuilder({ value, onChange }: BoxShadowBuilderProps) {
  const uid = useId();
  const [layers, setLayers] = useState<ShadowLayer[]>(() => {
    if (!value || value === "none") return [{ ...DEFAULT_LAYER }];
    return [{ ...DEFAULT_LAYER }];
  });

  const emit = useCallback(
    (updated: ShadowLayer[]) => {
      setLayers(updated);
      onChange(buildShadowCss(updated));
    },
    [onChange],
  );

  const updateLayer = useCallback(
    (index: number, patch: Partial<ShadowLayer>) => {
      const updated = layers.map((l, i) => (i === index ? { ...l, ...patch } : l));
      emit(updated);
    },
    [layers, emit],
  );

  const addLayer = useCallback(() => {
    emit([...layers, { ...DEFAULT_LAYER }]);
  }, [layers, emit]);

  const removeLayer = useCallback(
    (index: number) => {
      const updated = layers.filter((_, i) => i !== index);
      emit(updated.length > 0 ? updated : []);
    },
    [layers, emit],
  );

  const handlePreset = useCallback(
    (presetValue: string) => {
      onChange(presetValue);
      if (presetValue === "none") {
        setLayers([]);
      } else {
        setLayers([{ ...DEFAULT_LAYER }]);
      }
    },
    [onChange],
  );

  const currentCss = buildShadowCss(layers);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Layers className="h-3.5 w-3.5" />
        Box Shadow
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((preset) => (
          <button
            key={preset.name}
            type="button"
            onClick={() => handlePreset(preset.value)}
            className={[
              "rounded-md border px-2.5 py-1 text-[10px] font-medium",
              value === preset.value
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
            ].join(" ")}
          >
            {preset.name}
          </button>
        ))}
      </div>

      {/* Live preview */}
      <div className="flex h-24 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30">
        <div
          className="h-14 w-20 rounded-lg bg-[var(--color-card)]"
          style={{ boxShadow: currentCss === "none" ? undefined : currentCss }}
        />
      </div>

      {/* Shadow layers */}
      {layers.map((layer, index) => (
        <ShadowLayerControls
          key={index}
          uid={`${uid}-${index}`}
          index={index}
          layer={layer}
          canRemove={layers.length > 1}
          onUpdate={(patch) => updateLayer(index, patch)}
          onRemove={() => removeLayer(index)}
        />
      ))}

      {/* Add shadow */}
      <button
        type="button"
        onClick={addLayer}
        className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-[var(--color-border)] py-2 text-[10px] font-medium text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50 hover:text-[var(--color-primary)]"
      >
        <Plus className="h-3 w-3" />
        Add Shadow
      </button>

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          box-shadow: {currentCss};
        </code>
      </div>
    </div>
  );
}

// ── Individual layer controls ──
function ShadowLayerControls({ uid, index, layer, canRemove, onUpdate, onRemove }: {
  readonly uid: string; readonly index: number; readonly layer: ShadowLayer;
  readonly canRemove: boolean; readonly onUpdate: (patch: Partial<ShadowLayer>) => void;
  readonly onRemove: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
      {/* Layer header */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[var(--color-foreground)]">
          Shadow {index + 1}
        </span>
        <div className="flex items-center gap-2">
          {/* Inset toggle */}
          <button
            type="button"
            onClick={() => onUpdate({ isInset: !layer.isInset })}
            className={[
              "rounded-md border px-2 py-0.5 text-[9px] font-medium",
              layer.isInset
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-muted-foreground)]",
            ].join(" ")}
          >
            {layer.isInset ? "Inset" : "Outset"}
          </button>
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="rounded p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <SliderRow id={`${uid}-h`} label="H Offset" min={MIN_OFFSET} max={MAX_OFFSET}
        value={layer.hOffset} unit="px" onChange={(v) => onUpdate({ hOffset: v })} />
      <SliderRow id={`${uid}-v`} label="V Offset" min={MIN_OFFSET} max={MAX_OFFSET}
        value={layer.vOffset} unit="px" onChange={(v) => onUpdate({ vOffset: v })} />
      <SliderRow id={`${uid}-blur`} label="Blur" min={MIN_BLUR} max={MAX_BLUR}
        value={layer.blur} unit="px" onChange={(v) => onUpdate({ blur: v })} />
      <SliderRow id={`${uid}-spread`} label="Spread" min={MIN_SPREAD} max={MAX_SPREAD}
        value={layer.spread} unit="px" onChange={(v) => onUpdate({ spread: v })} />

      {/* Color + opacity */}
      <div className="flex items-center gap-2">
        <label
          htmlFor={`${uid}-color`}
          className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
        >
          Color
        </label>
        <input
          id={`${uid}-color`}
          type="color"
          value={normalizeColorInputValue(layer.color)}
          onChange={(e) => onUpdate({ color: e.target.value })}
          className="h-6 w-8 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0"
        />
        <input
          type="range"
          min={MIN_OPACITY}
          max={MAX_OPACITY}
          value={layer.opacity}
          onChange={(e) => onUpdate({ opacity: clamp(safeNum(e.target.value, layer.opacity), MIN_OPACITY, MAX_OPACITY) })}
          className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]"
        />
        <span className="w-8 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
          {layer.opacity}%
        </span>
      </div>
    </div>
  );
}

// ── Reusable slider row ──
function SliderRow({ id, label, min, max, value: val, unit, onChange: onSlide }: {
  readonly id: string; readonly label: string; readonly min: number;
  readonly max: number; readonly value: number; readonly unit: string;
  readonly onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">{label}</label>
      <input id={id} type="range" min={min} max={max} step={1} value={val}
        onChange={(e) => onSlide(clamp(safeNum(e.target.value, val), min, max))}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]" />
      <span className="w-12 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">{val}{unit}</span>
    </div>
  );
}
